/*
 * useVideoProgress
 * ────────────────
 * Track per-user watched % of a video and mark it "completed" at ≥80%.
 * Works with:
 *   • Native <video>            — listens for `timeupdate` + `ended`
 *   • YouTube iframe            — uses the YT IFrame API (lazy-loaded)
 *   • Vimeo iframe              — best-effort, no tracking (silent no-op)
 *
 * Public shape:
 *   {
 *     watchedSeconds, duration, percent, completed,
 *     initialLoaded,   // true once the initial GET resolves
 *   }
 *
 * Usage:
 *   const wrapRef = useRef(null);
 *   const p = useVideoProgress("course_intro", wrapRef, videoKind);
 *   // videoKind: "video" | "iframe" (auto-detected by IntroSection)
 *   // Then attach wrapRef to the container holding the <video>/<iframe>.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

const COMPLETION_THRESHOLD = 0.80;
const POST_INTERVAL_MS = 5000; // throttle progress POSTs
const YT_POLL_MS = 2000;

let ytApiPromise = null;
function loadYouTubeAPI() {
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) { resolve(window.YT); return; }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve(window.YT);
    };
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const s = document.createElement("script");
      s.src = "https://www.youtube.com/iframe_api";
      s.async = true;
      document.head.appendChild(s);
    }
  });
  return ytApiPromise;
}

export function useVideoProgress(progressKey, containerRef, videoKind) {
  const [state, setState] = useState({
    watchedSeconds: 0,
    duration: 0,
    percent: 0,
    completed: false,
    initialLoaded: false,
  });

  const lastPostAtRef = useRef(0);
  const bestSecondsRef = useRef(0);
  const durationRef = useRef(0);
  const completedRef = useRef(false);
  const ytPlayerRef = useRef(null);
  const ytPollRef = useRef(null);
  // Last `currentTime` we saw during a `timeupdate` tick. Used to
  // distinguish natural playback (delta ≤ ~1s per tick) from a
  // scrubber drag / keyboard skip (delta ≫ 1s). Without this gate,
  // dragging the scrubber to 5:00 would bump bestSecondsRef to 300
  // BEFORE the IntroSection clamp fires — that value would then
  // POST to the backend, permanently unlocking future skips.
  const lastTickRef = useRef(0);

  // ── Initial fetch of stored progress ──────────────────────────────
  useEffect(() => {
    if (!progressKey) return;
    let cancelled = false;
    api.get(`/video-progress/${progressKey}`)
      .then((r) => {
        if (cancelled) return;
        const d = r.data || {};
        bestSecondsRef.current = Number(d.watched_seconds || 0);
        durationRef.current = Number(d.duration || 0);
        completedRef.current = !!d.completed_at;
        setState({
          watchedSeconds: bestSecondsRef.current,
          duration: durationRef.current,
          percent: Number(d.percent || 0),
          completed: completedRef.current,
          initialLoaded: true,
        });
      })
      .catch(() => { if (!cancelled) setState((s) => ({ ...s, initialLoaded: true })); });
    return () => { cancelled = true; };
  }, [progressKey]);

  // ── Throttled POST to backend ─────────────────────────────────────
  const postProgress = useCallback(async (force = false) => {
    if (!progressKey) return;
    if (durationRef.current < 1 || bestSecondsRef.current < 1) return;
    const now = Date.now();
    if (!force && now - lastPostAtRef.current < POST_INTERVAL_MS) return;
    lastPostAtRef.current = now;
    try {
      const r = await api.post(`/video-progress/${progressKey}`, {
        watched_seconds: bestSecondsRef.current,
        duration: durationRef.current,
      });
      const d = r.data || {};
      const nextCompleted = !!d.completed_at;
      // Only re-render on meaningful state changes.
      completedRef.current = nextCompleted;
      setState((s) => {
        const nextPct = Number(d.percent || 0);
        if (s.percent === nextPct && s.completed === nextCompleted) return s;
        return {
          watchedSeconds: Number(d.watched_seconds || 0),
          duration: Number(d.duration || 0),
          percent: nextPct,
          completed: nextCompleted,
          initialLoaded: true,
        };
      });
    } catch { /* silent — retry on next tick */ }
  }, [progressKey]);

  // ── Native <video> event wiring ────────────────────────────────────
  useEffect(() => {
    if (!progressKey || videoKind !== "video") return;
    const container = containerRef.current;
    if (!container) return;
    const vid = container.querySelector("video");
    if (!vid) return;

    const onTime = () => {
      const dur = Number(vid.duration || 0);
      const cur = Number(vid.currentTime || 0);
      if (dur > 0) durationRef.current = dur;
      // Gate: only credit forward motion when it looks like natural
      // playback (0 < delta ≤ 1.0s). Anything larger is a seek —
      // ignore it so scrubber drags can't inflate watched_seconds.
      const prev = lastTickRef.current;
      const delta = cur - prev;
      if (delta > 0 && delta <= 1.0 && cur > bestSecondsRef.current) {
        bestSecondsRef.current = cur;
      }
      lastTickRef.current = cur;
      postProgress();
    };
    const onEnded = () => {
      const dur = Number(vid.duration || 0);
      if (dur > 0) {
        durationRef.current = dur;
        bestSecondsRef.current = Math.max(bestSecondsRef.current, dur);
      }
      postProgress(true);
    };
    const onLoaded = () => {
      const dur = Number(vid.duration || 0);
      if (dur > 0) durationRef.current = dur;
    };

    vid.addEventListener("timeupdate", onTime);
    vid.addEventListener("ended", onEnded);
    vid.addEventListener("loadedmetadata", onLoaded);
    return () => {
      vid.removeEventListener("timeupdate", onTime);
      vid.removeEventListener("ended", onEnded);
      vid.removeEventListener("loadedmetadata", onLoaded);
      postProgress(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressKey, videoKind, containerRef]);

  // ── YouTube iframe event wiring (via IFrame API) ───────────────────
  useEffect(() => {
    if (!progressKey || videoKind !== "iframe") return;
    const container = containerRef.current;
    if (!container) return;
    const iframe = container.querySelector("iframe");
    if (!iframe) return;
    // Only YouTube supported; Vimeo/others are silent no-ops.
    if (!/youtube\.com\/embed/.test(iframe.src)) return;

    // Ensure the iframe has JS API enabled.
    if (!/enablejsapi=1/.test(iframe.src)) {
      const sep = iframe.src.includes("?") ? "&" : "?";
      iframe.src = `${iframe.src}${sep}enablejsapi=1`;
    }

    let cancelled = false;
    let player = null;
    loadYouTubeAPI().then((YT) => {
      if (cancelled) return;
      player = new YT.Player(iframe, {
        events: {
          onReady: () => {
            durationRef.current = Number(player.getDuration?.() || 0);
          },
          onStateChange: (e) => {
            // PLAYING = 1
            if (e.data === 1 && !ytPollRef.current) {
              ytPollRef.current = setInterval(() => {
                const cur = Number(player.getCurrentTime?.() || 0);
                const dur = Number(player.getDuration?.() || 0);
                if (dur > 0) durationRef.current = dur;
                if (cur > bestSecondsRef.current) bestSecondsRef.current = cur;
                postProgress();
              }, YT_POLL_MS);
            } else if (e.data !== 1 && ytPollRef.current) {
              clearInterval(ytPollRef.current);
              ytPollRef.current = null;
              postProgress(true);
            }
            // ENDED = 0
            if (e.data === 0) {
              const dur = durationRef.current;
              if (dur > 0) bestSecondsRef.current = Math.max(bestSecondsRef.current, dur);
              postProgress(true);
            }
          },
        },
      });
      ytPlayerRef.current = player;
    });

    return () => {
      cancelled = true;
      if (ytPollRef.current) { clearInterval(ytPollRef.current); ytPollRef.current = null; }
      try { player?.destroy?.(); } catch {}
      postProgress(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressKey, videoKind, containerRef]);

  return state;
}
